import { create } from 'zustand';
import { DISPOSITION_LABELS } from '@launchpad/shared';

/** Sentinel value for filtering applications that have no disposition set */
export const NO_DISPOSITION = '__none__';

export const useFilterStore = create((set) => ({
  search: '',
  statusFilter: [],
  tagFilter: [],
  priorityFilter: [],
  dispositionFilter: [],
  dateRange: { from: '', to: '' },

  setSearch: (search) => set({ search }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setTagFilter: (tagFilter) => set({ tagFilter }),
  setPriorityFilter: (priorityFilter) => set({ priorityFilter }),
  setDispositionFilter: (dispositionFilter) => set({ dispositionFilter }),
  setDateRange: (dateRange) => set({ dateRange }),
  clearAll: () => set({ search: '', statusFilter: [], tagFilter: [], priorityFilter: [], dispositionFilter: [], dateRange: { from: '', to: '' } }),
}));

/**
 * Filter applications based on current filter state.
 * @param {Array} applications
 * @param {Object} filters - from useFilterStore
 * @returns {Array}
 */
export function applyFilters(applications, filters) {
  const { search, statusFilter, tagFilter, priorityFilter, dispositionFilter = [], dateRange } = filters;

  return applications.filter((app) => {
    // Text search (company, role, notes, disposition label + note)
    if (search) {
      const q = search.toLowerCase();
      const dispositionLabel = app.disposition ? (DISPOSITION_LABELS[app.disposition] || '') : '';
      const matches =
        (app.company || '').toLowerCase().includes(q) ||
        (app.role || '').toLowerCase().includes(q) ||
        (app.notes || '').toLowerCase().includes(q) ||
        dispositionLabel.toLowerCase().includes(q) ||
        (app.dispositionNote || '').toLowerCase().includes(q);
      if (!matches) return false;
    }

    // Status filter
    if (statusFilter.length > 0 && !statusFilter.includes(app.status)) return false;

    // Tag filter
    if (tagFilter.length > 0 && !tagFilter.some((t) => app.tags?.includes(t))) return false;

    // Priority filter
    if (priorityFilter.length > 0 && !priorityFilter.includes(app.priority)) return false;

    // Disposition filter (client-side, application board only).
    // NO_DISPOSITION matches records with no disposition set.
    if (dispositionFilter.length > 0) {
      const hasDisposition = !!app.disposition;
      const matchesDisposition =
        (hasDisposition && dispositionFilter.includes(app.disposition)) ||
        (!hasDisposition && dispositionFilter.includes(NO_DISPOSITION));
      if (!matchesDisposition) return false;
    }

    // Date range
    if (dateRange.from && app.dateApplied && app.dateApplied < dateRange.from) return false;
    if (dateRange.to && app.dateApplied && app.dateApplied > dateRange.to) return false;

    return true;
  });
}
